import { ApiHandlerService } from './api-hanler.service';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { ApiUrls } from '../utilities/APIUris';
import { HttpService } from './http.service';
import { LoginRequestInput, LoginResponse, OTPRequestInput, OTPRequestResponse, OTPVerifyRequestInput, RefreshTokenResponse, RegisterUserRequestInput, ResetPasswordRequest } from '../models/auth.model';
import { ProfileResponse } from '../models/profile.model';
import { NavigationService } from './navigation.service';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private userSubject: BehaviorSubject<any>;
  public user: Observable<any>;

  public profileSubject = new BehaviorSubject<any>(null);
  public profile: Observable<ProfileResponse> = this.profileSubject.asObservable();
  public loggedInUserId: string = '';
  public loggedInUserIdOrgId: string = '';
  public isOnboardingCompleted: boolean = false;

  public permissionList: any = {};

  constructor(
    private navigationService: NavigationService,
    private http: HttpService,
    private tokenService: TokenService,
    private ApiHandlerService: ApiHandlerService
  ) {
    this.userSubject = new BehaviorSubject(JSON.parse(localStorage.getItem('CueUser')!));
    this.user = this.userSubject.asObservable();
    this.profileSubject.subscribe((result: ProfileResponse) => {
      if (result) {
        this.loggedInUserId = result?.id;
        if (result && result.organization_users) {
          this.isOnboardingCompleted = result.organization_users && Object.keys(result.organization_users).length > 0 ? true : false;
          this.loggedInUserIdOrgId = result.organization_users.org_id;
        }
        if (result.auth_items) {
          this.permissionList = result.auth_items;
        }
      }
    });
  }

  public get userValue() {
    return this.userSubject.value;
  }

  public get profileValue(): ProfileResponse {
    return this.profileSubject.value;
  }

  ngOnDestroy(): void {
    throw new Error('Method not implemented.');
  }

  getPublicConfig() {
    return this.http.get(ApiUrls.publicConfig);
  }

  sendOtpRequest(requestPayload: OTPRequestInput): Observable<OTPRequestResponse> {
    return this.ApiHandlerService.doPost<OTPRequestResponse>(ApiUrls.sendOtpRequest, requestPayload, true, true);
  }


  verifyOTP(requestPayload: OTPVerifyRequestInput) {
    return this.http.post(ApiUrls.verifyOtpRequest, requestPayload);
  }

  registerUser(requestPayload: RegisterUserRequestInput) {
    return this.http.post(ApiUrls.registerUser, requestPayload);
  }

  doLogin(requestPayload: LoginRequestInput) {
    return this.ApiHandlerService.doPost(ApiUrls.applogin, requestPayload, true, true).pipe(
      map((user: any) => {
        this.tokenService.storeTokens(user);
        this.userSubject.next(user);
        return this.userValue;
      })
    );
  }

  getMyProfile(): Observable<any> {
    if (this.profileSubject.value) {
      return this.profile; // Return existing profile data
    } else {
      return this.refreshMyProfile();
    }
  }

  getMyProfileGuard(): Observable<any> {
    return this.ApiHandlerService.doGet(ApiUrls.getProfile, true, true).pipe(
      tap((profile) => {
        return profile;
      })
    );
  }

  refreshMyProfile(): Observable<any> {
    return this.ApiHandlerService.doGet(ApiUrls.getProfile, true, true).pipe(tap((profile) => this.profileSubject.next(profile)));
  }

  logout() {
    this.tokenService.removeTokens();
    this.userSubject.next(null);
    this.profileSubject.next(null);
    this.navigationService.navigateTo('/login');
  }

  refreshAccessToken(refresh_token: string): Observable<RefreshTokenResponse> {
    return this.http.post(ApiUrls.getAccessTokenFromRefreshToken, { refresh_token }).pipe(
      map((tokens: LoginResponse) => {
        this.tokenService.storeTokens(tokens);
        this.userSubject.next(tokens);
        return this.userValue;
      })
    );
  }

  forgotPassowrd(requestPayload: ResetPasswordRequest) {
    return this.http.put(ApiUrls.forgotResetPassowrd, requestPayload);
  }

  isUserLoggedIn() {
    return this.tokenService.isLoggedIn();
  }

  // Method to check permission
  checkHasPermission(module: string, permission: string): boolean {
    return this.permissionList?.[module]?.[permission] ?? false;
  }
}
